/**
 * Shell Plugin Styles
 *
 * Merged from the former core partials `core/styles/shell.css` +
 * `core/styles/tool-panel.css` (extraction #370). Near-verbatim concatenation —
 * cascade layer (`@layer tbw-base`) preserved so behavior is byte-identical
 * while both copies coexist during Phase 1a. Core copies are removed in
 * Task 1b.1.5; the layer question (tbw-base vs tbw-plugins) is revisited then.
 *
 * EXCEPTION: `.tbw-scroll-area` and `.tbw-grid-content` are NOT here — both are
 * core scroll-viewport layout (rendered by dom-builder for every grid, shell or
 * not) and live in `core/styles/base.css`. Without a shell, `.tbw-grid-content`
 * still wraps the scroll area, so its layout rule must load unconditionally.
 *
 * @layer tbw-base
 */

/* ==========================================================================
 * Shell Layout (was core/styles/shell.css)
 *
 * Styles for the grid shell including header, toolbar, content area,
 * and scroll container.
 * ========================================================================== */

@layer tbw-base {
  tbw-grid {
    .tbw-shell-header {
      display: flex;
      align-items: center;
      gap: 8px;
      min-height: var(--tbw-shell-header-height);
      padding: 0 8px;
      background: var(--tbw-shell-header-bg);
      border-bottom: 1px solid var(--tbw-shell-header-border);
      flex-shrink: 0;
    }

    .tbw-shell-title {
      font-size: var(--tbw-shell-title-font-size);
      font-weight: var(--tbw-shell-title-font-weight);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .tbw-shell-content {
      flex: 1;
      display: flex;
      align-items: center;
      gap: 12px;
      min-width: 0;
      overflow: hidden;
    }

    .tbw-shell-toolbar {
      display: flex;
      align-items: center;
      gap: var(--tbw-toolbar-button-gap);
      flex-shrink: 0;
    }

    .tbw-toolbar-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: var(--tbw-toolbar-button-size);
      height: var(--tbw-toolbar-button-size);
      padding: 0;
      border: 1px solid transparent;
      border-radius: var(--tbw-border-radius);
      background: transparent;
      color: var(--tbw-color-fg);
      cursor: pointer;
      font-size: 16px;
      transition:
        background var(--tbw-transition-duration) var(--tbw-transition-ease),
        border-color var(--tbw-transition-duration) var(--tbw-transition-ease);

      &:hover {
        background: var(--tbw-color-row-hover);
      }

      &:focus-visible {
        outline: var(--tbw-focus-outline);
        outline-offset: var(--tbw-focus-outline-offset);
      }

      &.active {
        background: var(--tbw-focus-background);
        border-color: var(--tbw-color-accent);
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    .tbw-toolbar-separator {
      width: 1px;
      height: 20px;
      background: var(--tbw-color-border);
      margin: 0 4px;
    }

    .tbw-shell-body {
      position: relative;
      display: flex;
      flex: 1;
      min-height: 0;
      overflow: visible;
    }
  }
}

/* ==========================================================================
 * Tool Panel (was core/styles/tool-panel.css)
 *
 * Styles for the collapsible side panel including header, content,
 * resize handle, and accordion components.
 * ========================================================================== */

@layer tbw-base {
  tbw-grid {
    .tbw-tool-panel {
      position: absolute;
      top: 0;
      bottom: 0;
      right: 0;
      width: 0;
      /* `clip` rather than `hidden` so the clip region can be widened: the
         splitter's hit area straddles the docked edge and has to survive it.
         `overflow-clip-margin` is the one place `calc()` is avoided — Chromium
         drops the declaration when the value is calculated, so the overhang is
         its own token and the splitter geometry is derived from it. */
      overflow: clip;
      overflow-clip-margin: var(--tbw-tool-panel-resize-overhang, 12px);
      background: var(--tbw-tool-panel-bg);
      border-left: 1px solid var(--tbw-tool-panel-border);
      transition: width var(--tbw-tool-panel-transition);
      display: flex;
      flex-direction: column;
      z-index: var(--tbw-z-layer-toolpanel, 31);
      box-shadow: -2px 0 8px var(--tbw-color-shadow);

      &[data-position='left'] {
        right: auto;
        left: 0;
        border-left: none;
        border-right: 1px solid var(--tbw-tool-panel-border);
        box-shadow: 2px 0 8px var(--tbw-color-shadow);
      }

      &.open {
        /* Never wider than the grid it docks into. An overflowing panel is cut
           off by the clip above with no way to scroll to what is missing — the
           labels and the left half of every chip simply vanish (SC 1.4.10
           Reflow). The 20px reserve matches the drag clamp in
           `setupToolPanelResize`, so no path can reach a size the other cannot.
           `max-width` as well as `width`: a dragged width lands as an inline
           style, which only a max-width can rein back in when the grid later
           narrows under it. */
        width: min(var(--tbw-tool-panel-width), 100% - 20px);
        max-width: calc(100% - 20px);
      }
    }

    /* ========== Push mode ========== */

    /* In push mode the panel becomes a flex sibling of the grid content
       instead of overlaying it. Opening the panel shrinks the grid's
       available width; the grid's ResizeObserver re-runs column
       virtualization, so cells that would be hidden by an overlay panel
       remain reachable.

       The overlay shadow is dropped (nothing to lift off) and the resize
       handle is suppressed: the panel's width is part of the layout, so
       drag-to-resize would fight the flex container instead of cooperating
       with it. */
    .tbw-shell-body[data-mode='push'] {
      .tbw-tool-panel {
        position: relative;
        top: auto;
        bottom: auto;
        right: auto;
        left: auto;
        flex-shrink: 0;
        box-shadow: none;
        z-index: auto;
        /* In overlay mode the 1px side border is harmless because the panel
           floats above the grid. In push mode the panel is a flex sibling,
           so a non-zero border on a width:0 panel still reserves 1px of
           horizontal space and shows as a stray divider. Drop the border
           by default and only re-apply it on the open state. */
        border-left: none;
        border-right: none;

        &.open {
          border-left: 1px solid var(--tbw-tool-panel-border);
        }

        &[data-position='left'].open {
          border-left: none;
          border-right: 1px solid var(--tbw-tool-panel-border);
        }
      }

      .tbw-tool-panel-resize {
        display: none;
      }
    }

    /* ========== Dropdown mode ========== */

    /* In dropdown mode the whole tool-panel sidebar (the full accordion) is
       promoted to the top layer as a native popover and anchored near a
       trigger (the built-in toggle, a custom column-header button, or the grid
       corner). The docked absolute/full-height sidebar styling is replaced by
       a self-sized floating card with an internal scroll region.

       The resize handle is suppressed (a popover has no docked edge to drag)
       and the open/close width transition is dropped (the popover is shown and
       hidden via the Popover API, not by animating width). */
    .tbw-tool-panel[popover] {
      position: fixed;
      inset: auto;
      width: var(--tbw-tool-panel-width);
      max-width: calc(100vw - 16px);
      height: auto;
      max-height: min(70vh, 480px);
      overflow: hidden auto;
      margin: 4px 0 0 0;
      padding: 0;
      border: 1px solid var(--tbw-tool-panel-border);
      border-radius: var(--tbw-border-radius, 6px);
      box-shadow: 0 8px 24px var(--tbw-color-shadow);
      transition: none;

      /* The native popover UA rule hides a closed popover with `display: none`,
         but the base `.tbw-tool-panel { display: flex }` author rule outranks
         the UA origin and would leak the closed popover onto the page (floating
         over the header). Re-assert hidden-until-open here, and restore the
         flex layout only while the popover is actually shown. */
      display: none;

      /* `:popover-open` is the native (Popover API) open signal. `.open` is the
         JS-toggled fallback (`updatePanelState`) so dropdown mode still renders
         in browsers / DOMs without Popover API support, where `showPopover()`
         is unavailable and `:popover-open` never matches. */
      &:popover-open,
      &.open {
        display: flex;
        width: var(--tbw-tool-panel-width);
      }

      .tbw-tool-panel-resize {
        display: none;
      }
    }

    /* CSS anchor positioning (progressive enhancement). When the browser
       supports it, the controller wires `anchor-name` on the trigger and
       `position-anchor` on the popover; JS only sets fixed coordinates as a
       fallback for browsers without anchor positioning. */
    @supports (anchor-name: --x) {
      .tbw-tool-panel[popover] {
        /* Default (right position): align popover's right edge to the anchor's
           right edge and drop below it. */
        top: anchor(bottom);
        right: anchor(right);
        left: auto;
        position-try-fallbacks:
          flip-block,
          flip-inline,
          flip-block flip-inline;

        /* Grid-corner fallback anchors to the top of the grid host. */
        &[data-anchor='corner'] {
          top: anchor(top);
        }

        &[data-position='left'] {
          left: anchor(left);
          right: auto;
        }
      }
    }

    .tbw-tool-panel-resize {
      position: absolute;
      top: 0;
      bottom: 0;
      /* Transparent hit area, twice the overhang wide (24px by default, the
         SC 2.5.8 minimum) and centred on the docked edge, so half of it falls
         outside the panel and it never covers the panel's own controls. */
      width: calc(var(--tbw-tool-panel-resize-overhang, 12px) * 2);
      cursor: col-resize;
      background: transparent;
      z-index: 10;
      /* The splitter drag is driven by pointer capture, so the browser must not
         claim the gesture for panning on touch devices. */
      touch-action: none;
      user-select: none;

      /* The visible bar is only the 6px at the docked edge; the rest of the
         width is invisible hit area. */
      &::before {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        width: 6px;
        background: transparent;
        transition: background var(--tbw-transition-duration) var(--tbw-transition-ease);
      }

      &[data-handle-position='left'] {
        left: calc(3px - var(--tbw-tool-panel-resize-overhang, 12px));

        &::before {
          left: calc(var(--tbw-tool-panel-resize-overhang, 12px) - 3px);
        }
      }

      &[data-handle-position='right'] {
        right: calc(3px - var(--tbw-tool-panel-resize-overhang, 12px));

        &::before {
          right: calc(var(--tbw-tool-panel-resize-overhang, 12px) - 3px);
        }
      }

      /* `[data-tbw-size-open]` keeps the splitter lit while the size popover is
         open, so the popover stays visually tied to the edge it is sizing. */
      &:hover::before,
      &.resizing::before,
      &[data-tbw-size-open]::before {
        background: var(--tbw-color-accent);
      }
    }

    /* A closed panel is zero-width, so the splitter would hang over the grid
       inside the clip margin. It has nothing to size until the panel is open. */
    .tbw-tool-panel:not(.open) .tbw-tool-panel-resize {
      display: none;
    }

    .tbw-tool-panel-header {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      min-height: var(--tbw-tool-panel-header-height);
      padding: 0 12px;
      border-bottom: 1px solid var(--tbw-tool-panel-border);
      flex-shrink: 0;
    }

    .tbw-tool-panel-title {
      font-weight: 600;
      font-size: 13px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .tbw-tool-panel-close {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      padding: 0;
      border: none;
      border-radius: var(--tbw-border-radius);
      background: transparent;
      color: var(--tbw-color-fg-muted);
      cursor: pointer;
      font-size: 14px;

      &:hover {
        background: var(--tbw-color-row-hover);
        color: var(--tbw-color-fg);
      }
    }

    /* Inline close button — shares the first accordion header row in
       single-panel mode (header bar hidden, overlay/push mode). */
    .tbw-accordion-header-row {
      display: flex;
      align-items: stretch;

      .tbw-accordion-header {
        flex: 1;
        min-width: 0;
      }
    }

    .tbw-tool-panel-close--inline {
      align-self: center;
      flex-shrink: 0;
      margin-inline-end: 8px;
    }

    .tbw-tool-panel-content {
      flex: 1;
      overflow: auto;
    }

    /* ========== Accordion ========== */

    .tbw-accordion {
      display: flex;
      flex-direction: column;
      gap: 0;
    }

    .tbw-accordion-section {
      border-bottom: 1px solid var(--tbw-tool-panel-border);

      &:last-child {
        border-bottom: none;
      }

      &.single .tbw-accordion-header {
        cursor: default;

        &:hover {
          background: transparent;
        }
      }

      &.expanded {
        .tbw-accordion-chevron {
          transform: rotate(90deg);
        }

        .tbw-accordion-content {
          display: block;
        }
      }
    }

    .tbw-accordion-header {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      padding: 10px 12px;
      border: none;
      background: transparent;
      color: var(--tbw-color-fg);
      font-size: 13px;
      font-weight: 600;
      text-align: start;
      cursor: pointer;
      user-select: none;

      &:hover {
        background: var(--tbw-color-row-hover);
      }
    }

    .tbw-accordion-chevron {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 16px;
      font-size: 10px;
      color: var(--tbw-color-fg-muted);
      transition: transform 0.15s ease;
      flex-shrink: 0;
    }

    .tbw-accordion-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 16px;
      font-size: 14px;
      flex-shrink: 0;
    }

    .tbw-accordion-title {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .tbw-accordion-content {
      display: none;
    }
  }
}
